<script lang="ts" setup>

import { ref, computed, shallowRef, onMounted, onBeforeUnmount, watch, provide, nextTick, useTemplateRef } from 'vue';
import { useRoute } from 'vue-router'; // Added import

// ... other imports ...
import { Map, LngLatLike, LngLatBounds, CameraOptions, AttributionControl, GeolocateControl, MapMouseEvent, MapTouchEvent, FlyToOptions, setWorkerUrl } from 'maplibre-gl';
import mapLibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url';
import { useStore } from '@/store/Store';
import { useLiveStore } from '@/store/LiveStore';
import { EventBus } from '@/utils/EventBus';
import * as loc from '@/RunnerLocation';
import { sleep } from "../utils/Utils";
import { getArrowMarkersAlong, getKmMarkersAlong } from "@/Util";
import { SatelliteControl } from "../controls/SatelliteControl";

// Component Imports
import MapTrack from "./player/overlays/MapTrack.vue";
import LocationTrack from "./player/overlays/LocationTrack.vue";
import HeightCurve from "./player/HeightCurve.vue";
import MapPolygons from "./player/overlays/MapPolygons.vue";
import FeatureDetails from "./player/FeatureDetails.vue";
import LayerView from "./player/LayerView.vue";
import TopBar from "./player/TopBar.vue";
import MoreInfo from "./player/MoreInfo.vue";
import MapSymbols from './player/overlays/MapSymbols.vue';
import MarkerCanvas from './player/overlays/MarkerCanvas.vue';
import ContourLines from './player/overlays/ContourLines.vue';

import 'maplibre-gl/dist/maplibre-gl.css';
import { useProgressStore } from '@/store/ProgressStore';
import { TFeature, TFeatureCollection, TLineString, TPolygon } from '@/shared/types/geojson';
import { getBounds } from '@/utils/geojson';
import { captureMessage } from '@/sentry/Sentry';
import PaceCalculator from './PaceCalculator.vue';

setWorkerUrl(mapLibreWorkerUrl);

// Props definition
const props = defineProps<{
	styleId?: string;
	editMode?: boolean;
	editor?: boolean;
	selectedFeatureIds?: string[];
}>();

// Emits definition (Vue 3.3+)
const emit = defineEmits<{
	positionError: [];
}>();

defineExpose({
	zoomTo
})

// Store instances
const store = useStore();
const liveStore = useLiveStore();
const route = useRoute();
const progressStore = useProgressStore()

// Template Refs
const wrapper = useTemplateRef("wrapper");
const mapRef = useTemplateRef("mapRef");

// Map instance
const mapShallow = shallowRef<Map | undefined>(undefined);

// Data refs
const createError = ref(false);
const initialized = ref(false);
const zoomTarget = shallowRef<"all" | TFeature | TFeature[] | null>(null);
const screenWidth = ref(0);
const elWidth = ref(0);
const elHeight = ref(0);
const skipFirstAnimation = ref(false);
const trackingLocation = ref(false);
const layerViewOpen = ref(false);
const layerViewSlideOffset = ref<number | null>(null);
const awaitingPopupId = ref<string | number | undefined>(undefined);
const satelliteControl = shallowRef(new SatelliteControl());
const geolocateControl = shallowRef<GeolocateControl | null>(null); // Ref for GeolocateControl
let hasZoomedToLiveResult = false;

let hasLocated = false; // Keep as let since it's module-scoped state

const kmMarkers = computed(() => {
	const useMiles = mapData.value?.unit == "mi";
	return getKmMarkersAlong(store.trackFeature, 1, useMiles, store.trackLength);
});

const arrowMarkers = computed((): TFeatureCollection => {
	const useMiles = mapData.value?.unit == "mi";
	return getArrowMarkersAlong(store.trackFeature, useMiles);
});

const iframed = computed(() => {
	//Midnattsloppet results page uses this
	if (route.query.allowinteraction == "1")
		return false;
	//for testing iframed mode
	if (document.location.href.indexOf("_iframed") > -1)
		return true;

	// https://stackoverflow.com/a/326076
	try {
		return window.self !== window.top;
	} catch (e) {
		return true;
	}
});

// Allow scroll events to propagate to parent page when iframed
watch(iframed, (val) => {
	const style = val ? 'auto' : 'none';
	document.documentElement.style.overscrollBehavior = style;
	// document.body.style.overscrollBehavior = style;
}, { immediate: true });

const isSmall = computed(() => {
	return elWidth.value < 500 && route.query.showroutes != "1";
});

const isTest = computed(() => {
	return route.query.test == "1";
});

const allowAllInteractions = computed(() => {
	return !iframed.value || route.query.allowzoom == "1";
});

const liveId = computed(() => {
	return route.query.liveId as string || "";
});

const sizeClass = computed(() => {
	//try to detect ipads and hide the sidebar on them.
	const width = elWidth.value;
	const height = elHeight.value;
	const maxSide = Math.max(width, height);
	const minSide = Math.min(width, height);
	const aspectRatio = minSide > 0 ? maxSide / minSide : 0;
	const isCompactScreen = width > 0 && height > 0
		&& maxSide <= 1366
		&& minSide >= 650
		&& aspectRatio <= 1.8;

	if (isCompactScreen)
		return "size-s";

	if (width > 1200)
		return "size-l";
	else if (width > 950)
		return "size-m";
	return "size-s";
});

const slideMode = computed(() => {
	return sizeClass.value == "size-s" || iframed.value;
});

const layerViewSlideStyle = computed(() => {
	if (layerViewSlideOffset.value == null) return undefined;
	return {
		left: `${-layerViewSlideOffset.value}px`,
		transition: "none",
	};
});

watch(layerViewOpen, (open) => {
	if (open) return;
	// Allow one paint at the dragged offset before releasing to the CSS closed position.
	requestAnimationFrame(() => {
		layerViewSlideOffset.value = null;
	});
});

// Pinia state access (direct)
const mapData = computed(() => store.map);


// Provide map instance
provide('$map', mapShallow);

// Methods
function initInteractions() {
	const map = mapShallow.value;
	if (!map) return;
	map.touchZoomRotate.disableRotation();
	map.dragRotate.disable();
	map.touchPitch.disable();
}

function initMap() {
	if (initialized.value)
		return;

	initialized.value = true;
	progressStore.progress = 1;
	if (!props.editMode && !skipFirstAnimation.value && !window["__map_bounds__"]) {
		progressStore.animate(isTest.value);
	}

	if (awaitingPopupId.value != undefined) {
		nextTick(() => {
			EventBus.emit("showPopup", { id: awaitingPopupId.value });
			awaitingPopupId.value = undefined;
		});
	}
}

/**
 * Zoom the map to a specific feature or set of features. "all" zooms to the currently selected route.
 */
async function zoomTo(target: "all" | TFeature | TFeature[], speed: "normal" | "fast" | "immediate" = "normal", zoom?: number, offset?: [number, number]) {
	if (!mapShallow.value) return;

	let cameraOptions: CameraOptions = {};

	let features: TFeature[];
	if (target == "all") {
		features = [store.trackFeature];
		let foundSlug = false;
		//special case: if there's a shape with the same slug as the route, zoom to the shape bounds.
		if (store.trackFeature.properties.slug != "") {
			const f = store.featuresBySlug.get(store.trackFeature.properties.slug);
			if (f && f.id != features[0].id) {
				foundSlug = true;
				features = [f];
			}
		}
		// another special case: if the selected router has 0 length, zoom to the bounds of all routes.
		// Lidingöloppet uses this.
		if (!foundSlug && store.actualTrackLength == 0) {
			features = store.routes
		}
	} else if (!Array.isArray(target)) {
		features = [target];
	} else {
		features = target;
	}

	// if features are actually shapes with generated geometry, replace with the actual generated geometry.
	features = features.map(f => f.properties.shapeType ? store.computedShapes.find(s => s.id === f.id)! : f);

	if (features.length == 1 && features[0].geometry.type == "Point" && !features[0].properties.shapeType) {
		const targetZoom = screenWidth.value > 600 ? 17 : 16;
		const center = features[0].geometry.coordinates as LngLatLike;
		const currentZoom = mapShallow.value.getZoom();
		const point = mapShallow.value.project(center);
		const { clientWidth, clientHeight } = mapShallow.value.getContainer();
		const nearby =
			point.x >= -clientWidth &&
			point.x <= clientWidth * 2 &&
			point.y >= -clientHeight &&
			point.y <= clientHeight * 2;
		cameraOptions = {
			center,
			zoom: nearby && currentZoom > targetZoom ? currentZoom : targetZoom,
		};
	} else {
		const bounds = getBounds(features)

		const widthFactor = window.innerWidth < 300 ? 0.2 : window.innerWidth < 500 ? 0.5 : 1;
		const heightFactor = window.innerHeight < 300 ? 0.2 : window.innerHeight < 500 ? 0.8 : 1;

		let padding = {
			left: 40 * widthFactor,
			right: 40 * widthFactor,
			top: 70 * heightFactor,
			bottom: 120 * heightFactor,
		};

		//TODO: revisit this: fix for very small screen sizes breaking the map.
		if (window.innerHeight < 300)
			padding.top = padding.bottom = 40;
		if (window.innerHeight < 90)
			padding.top = padding.bottom = 0;

		if (bounds.isEmpty() || bounds.getNorth() == 0)
			return;

		cameraOptions = mapShallow.value.cameraForBounds(bounds, { padding }) as CameraOptions;
		if (!cameraOptions) {
			await sleep(700);
			cameraOptions = mapShallow.value.cameraForBounds(bounds, { padding }) as CameraOptions;
		}

		// Handle case where cameraForBounds returns undefined (e.g., padding too large)
		if (!cameraOptions) {
			captureMessage(`Failed to get camera options for bounds: ${JSON.stringify(bounds)}, padding: ${JSON.stringify(padding)}, screenWidth: ${window.innerWidth}, screenHeight: ${window.innerHeight}`);
			cameraOptions = {
				center: bounds.getCenter(),
				zoom: 12
			}
		}
	}

	cameraOptions.pitch = 0;
	cameraOptions.bearing = 0;
	if (zoom !== undefined) cameraOptions.zoom = zoom;

	if (speed == "immediate") {
		await mapShallow.value.jumpTo(cameraOptions);
	}
	else {
		const flyToOptions: FlyToOptions = { ...cameraOptions };
		if (offset) flyToOptions.offset = offset;
		if (speed == "fast") flyToOptions.speed = 2.5;

		//nexttick to make heavy rendering on current frame
		// not affect the animation.
		nextTick(() => {
			mapShallow.value?.flyTo(flyToOptions);
		});
	}

	zoomTarget.value = target;
}

function checkLoaded() {
	if (mapShallow.value?.loaded() && mapShallow.value?.areTilesLoaded()) {
		initMap();
	}
}

function updateSize() {
	//refactor: we get this from the parent (mapview / mapeditor)
	const el = document.getElementsByClassName("map-parent")[0] as HTMLElement;
	screenWidth.value = window.innerWidth;
	elWidth.value = el?.clientWidth ?? 0;
	elHeight.value = (wrapper.value as HTMLElement)?.clientHeight ?? 0;
}

function locateMe() {
	zoomTarget.value = null;
	geolocateControl.value?.trigger();
}

function disableGeolocate() {
	if (!geolocateControl.value) return;

	if (geolocateControl.value._watchState != "OFF")
		geolocateControl.value.trigger();
	// if we were in "background" mode we need to trigger again... :/

	if (geolocateControl.value._watchState != "OFF")
		geolocateControl.value.trigger();
	//cancels animation that could get triggered by the above, if in background mode...
	if (mapShallow.value) {
		mapShallow.value.setZoom(mapShallow.value.getZoom());
	}
}

function zoomToSlug(slug: string) {
	const feature = store.featuresBySlug.get(slug);
	if (!feature) {
		console.warn(`[Map] postMessage slug not found: ${slug}`);
		return;
	}
	zoomTo(feature);
}

function handleWindowMessage(event: MessageEvent) {
	if (!event.data || typeof event.data !== "object") {
		return;
	}

	if (iframed.value && typeof event.data.slug === "string") {
		zoomToSlug(event.data.slug);
		return;
	}

	if (event.data.type == "markers") {
		liveStore.updateResults(event.data.message);
		//fixes flickering labels when updating markers while zoomed in.
		if (mapShallow.value) {
			mapShallow.value._fadeDuration = 0;
		}
		nextTick(() => {
			EventBus.emit("updatePopup");
		});
		if (liveId.value != "") {
			if (!hasZoomedToLiveResult) {
				hasZoomedToLiveResult = true;
				const feature = liveStore.resultFeatures.find(f => f.id == liveId.value);
				if (feature) {
					zoomTo(feature, "immediate");
					if (initialized.value) {
						EventBus.emit("showPopup", { id: feature.id });
					} else {
						awaitingPopupId.value = feature.id;
					}
				}
			}
		}
	}

	if (event.data.type == "click") {
		EventBus.emit("closePopup");
		EventBus.emit("showPopup", { id: event.data.message.startNo });
	}
}

// Watchers
watch(() => store.trackFeature, () => {
	loc.updateLocation(undefined, store.trackFeature, store.actualTrackLength);
}, { immediate: true });

watch(() => props.editMode, (isEditMode) => {
	if (isEditMode) {
		progressStore.stopAnimation();
	} else {
		progressStore.animate(isTest.value);
	}
	disableGeolocate();
});

watch(() => store.videoMode, () => {
	progressStore.animate(isTest.value);
});

watch(() => store.map?.satellite, (isSatellite) => {
	if (!mapShallow.value || !geolocateControl.value) return;
	if (isSatellite) {
		mapShallow.value.addControl(satelliteControl.value, "bottom-right");
		//put back in order
		mapShallow.value.removeControl(geolocateControl.value);
		mapShallow.value.addControl(geolocateControl.value, "bottom-right");
	} else {
		mapShallow.value.removeControl(satelliteControl.value);
	}
});

watch(sizeClass, () => {
	nextTick(() => {
		mapShallow.value?.resize();
	});
});


// Lifecycle Hooks
onMounted(() => {
	window.addEventListener("message", handleWindowMessage, false);

	const queryLang = new URLSearchParams(window.location.search).get("lang");
	store.setRequestedLanguage(queryLang);

	let mapInstance: Map | undefined = undefined;
	try {
		mapInstance = new Map({
			container: mapRef.value as HTMLElement,
			style: store.basemap.url,
			attributionControl: false,
			fadeDuration: 100,
			maxCanvasSize: isTest.value ? [1000000000, 1000000000] : [4096, 4096],
			canvasContextAttributes: {
				antialias: true,
				preserveDrawingBuffer: isTest.value,
			},
			cooperativeGestures: !allowAllInteractions.value,
			locale: {
				'CooperativeGesturesHandler.WindowsHelpText': store.uiText.desktop,
				'CooperativeGesturesHandler.MacHelpText': store.uiText.desktopMac,
				'CooperativeGesturesHandler.MobileHelpText': store.uiText.mobile,
			},
		});
	} catch (err) {
		console.log("no webgl?", err);
		createError.value = true;
	}
	if (!mapInstance) return;

	mapShallow.value = mapInstance; // Assign to shallowRef
	//TODO: only keep in store instead of providing?
	store.mapInstance = mapInstance;

	const attribution = new AttributionControl({
		compact: true,
		customAttribution: `<a href='https://www.racedaymap.com' target='_blank'>racedaymap.com</a>`,
	});
	mapInstance.addControl(attribution, "bottom-left");
	//@ts-ignore // _updateCompactMinimize is private
	attribution._updateCompactMinimize();
	// .addControl(new NavigationControl({showCompass:false}));

	initInteractions();
	updateSize();


	const routeSlug = route.params.slug1;
	let markerSlug = route.params.slug2;
	let foundRoute = false;
	if (routeSlug) {
		for (const r of mapData.value!.routes) {
			if (r.properties?.slug == routeSlug) {
				store.setSelectedRouteId(r.id as string);
				foundRoute = true;
				break;
			}
		}

		if (!foundRoute && !markerSlug)
			markerSlug = routeSlug;

	}

	let foundMarker = false;
	if (markerSlug) {
		const feature = store.featuresBySlug.get(markerSlug as string);
		if (feature) {
			foundMarker = true;
			zoomTo(feature as any, "immediate");
			awaitingPopupId.value = feature.id;
		}
	}
	if (!foundMarker) {
		// hack when switching routes in editor or switching to satellite
		if (window["__map_bounds__"]) {
			mapInstance.fitBounds(window["__map_bounds__"], { animate: false });
		} else if (!mapData.value?.routes?.length && mapData.value?.center != undefined) {
			mapShallow.value.jumpTo({
				center: mapData.value.center,
				zoom: 8,
				bearing: 0,
				pitch: 0,
			});
		} else {
			zoomTo("all", "immediate");
		}
	}
	skipFirstAnimation.value = foundMarker || liveId.value != "";



	const geolocate = new GeolocateControl({
		positionOptions: {
			enableHighAccuracy: true,
		},
		fitBoundsOptions: {
			maxZoom: 17,
		},
		trackUserLocation: true,
		showAccuracyCircle: true,
	});
	geolocateControl.value = geolocate;

	//GEOLOCATE BUTTON

	geolocate.on('geolocate', (evt) => {
		loc.updateLocation(evt, store.trackFeature, store.actualTrackLength);
	});

	//error is GeolocationPositionError, but that fails in ts build...?
	geolocate.on('error', (evt: any) => {
		//TODO: handle other evt.code errors?
		if (evt.code == evt.PERMISSION_DENIED) {
			emit("positionError");
		}
		trackingLocation.value = false;
	});

	geolocate.on('trackuserlocationstart', () => {
		trackingLocation.value = true;
	});

	geolocate.on('trackuserlocationend', () => {
		trackingLocation.value = false;
	});

	if (mapData.value!.satellite)
		mapInstance.addControl(satelliteControl.value, "bottom-right");

	mapInstance.addControl(geolocate, "bottom-right");

	// mapInstance.on("load", () => {
	// 	if (props.editMode && route.meta?.isNew && !hasLocated) {
	// 		hasLocated = true;
	// 		geolocate.trigger();
	// 	}
	// });

	// //for testing location tracks
	// mapInstance.on("click", evt => {
	// 	const pos:any = {};
	// 	pos.type = "geolocate";
	// 	pos.timestamp = new Date().getTime();
	// 	pos.coords = {longitude: evt.lngLat.lng, latitude:evt.lngLat.lat, accuracy: 12};
	// 	loc.updateLocation(pos, trackFeature.value, actualTrackLength.value)
	// })

	// HACK FIX FOR CIRCLE RADIUS FLICKERING. (unprojecting 100px instead of 1px)
	//@ts-ignore
	GeolocateControl.prototype._updateCircleRadius = function () {
		//@ts-ignore
		const y = this._map._container.clientHeight / 2;
		//@ts-ignore
		const a = this._map.unproject([0, y]);
		//@ts-ignore
		const b = this._map.unproject([100, y]);
		const metersPerPixel = a.distanceTo(b) / 100;
		//@ts-ignore
		const circleDiameter = Math.ceil(2.0 * this._accuracy / metersPerPixel);
		//@ts-ignore
		this._circleElement.style.width = `${circleDiameter}px`;
		//@ts-ignore
		this._circleElement.style.height = `${circleDiameter}px`;
	};


	mapInstance.on('load', checkLoaded);

	mapInstance.on("movestart", (evt) => {
		if (evt.originalEvent && evt.originalEvent.type != "resize") {
			zoomTarget.value = null;
		}
	});

	mapInstance.on("click", (evt) => {
		EventBus.emit("closePopup");
	});

	mapInstance.on("resize", (evt) => {
		if (zoomTarget.value) {
			zoomTo(zoomTarget.value);
		}
		updateSize();
	});

	progressStore.progress = 0;

	EventBus.on("routeClicked", () => {
		zoomTo("all");
		progressStore.animate(isTest.value);
	});

	EventBus.on("zoomTo", (f) => {
		if (typeof (f) == "object" && "features" in f) {
			zoomTo(f.features, f.speed, f.zoom, f.offset);
		} else {
			zoomTo(f);
		}
	});


});

onBeforeUnmount(() => {
	window.removeEventListener("message", handleWindowMessage, false);
	if (mapShallow.value) {
		window["__map_bounds__"] = mapShallow.value.getBounds();
		mapShallow.value.remove();
	}
});

</script>


<template>
	<div class="mapContainer"
		:class="[{ videoMode: store.videoMode, 'edit-mode': editMode, 'elevation': store.hasElevation, open: layerViewOpen, slide: slideMode || editMode, iframed }, sizeClass]"
		:style="layerViewSlideStyle">
		<div class="side">
			<layer-view v-if="!createError" :open="layerViewOpen" :slideMode="slideMode" @toggle="layerViewOpen = $event"
				@slide-offset="layerViewSlideOffset = $event" :iframed="iframed" :editor="editor" />
		</div>
		<div ref="wrapper" class="wrapper">
			<top-bar v-if="!editMode && !store.videoMode && initialized" :open="layerViewOpen" :slide="slideMode"
				@toggle="layerViewOpen = $event" @locateMe="locateMe" :isZoomedToRoute="zoomTarget === 'all'" :iframed="iframed"
				:small="isSmall" />
			<div ref="mapRef" class="map" /> <!-- Changed ref name -->

			<template v-if="initialized && mapShallow">


				<map-track :progress="progressStore.progress" :progressMode="progressStore.progressMode"
					@setProgress="progressStore.setProgress" :editMode="editMode" />
				<div> <!-- div needed since the v-if messes up marker visibility otherwise (when toggling edit mode) -->
					<location-track v-if="!editMode" />
				</div>
				<map-polygons />

				<contour-lines v-if="store.basemap.contourLines && mapData?.contourLines" />

				<map-symbols :kmFeatures="kmMarkers" :arrowFeatures="arrowMarkers" :progress="progressStore.progress"
					:progressMode="progressStore.progressMode" :editMode="editMode" />

				<marker-canvas :features="[...store.computedMarkers, ...liveStore.resultFeatures]"
					:progress="progressStore.progress" :progressMode="progressStore.progressMode"
					:actualTrackLength="store.actualTrackLength" :editMode="editMode" :selectedFeatureIds="selectedFeatureIds" />

				<height-curve v-if="!editMode && store.heightData && store.hasElevation && store.trackFeature"
					:progress="progressStore.progress" :progressMode="progressStore.progressMode" :raceLength="store.trackLength"
					:miles="mapData?.unit == 'mi'" :colorOverride="store.heightColorOverride"
					@setProgress="progressStore.setProgress" :heightData="store.heightData" :legs="store.trackFeatureLegs"
					:overrideMin="store.trackFeature.properties.heightMin" :overrideMax="store.trackFeature.properties.heightMax"
					:markers="store.computedMarkersNotGenerated" />
				<slot></slot> <!-- edit-tools are injected here in MapEditor. -->
				<feature-details :features="[...store.computedMarkers, ...liveStore.resultFeatures]" />
			</template>

			<MoreInfo />
			<PaceCalculator />

			<!-- <div style="position:relative;z-index:100000000000">
				{{ progressMode }}
				{{locationProgress}}

			</div> -->
		</div>
	</div>
</template>



<style scoped>
.map {
	position: absolute;
	width: 100%;
	height: 100%;
}

.mapContainer {
	position: absolute;
	left: 0;
	top: 0;
	width: 100%;
	height: 100%;
	z-index: 1000;

	display: flex;
	flex-flow: row nowrap;


	overflow: hidden;
}

.mapContainer.slide {
	transition: left .2s ease-in-out;
	left: max(var(--sidebar-width) * -1, calc(var(--map-peek) - 100vw));
	width: calc(100% + min(var(--sidebar-width), calc(100vw - var(--map-peek))));
}

.mapContainer.slide.open {
	left: 0px;
}

.wrapper {
	/* position: relative;
		width: 100%;
		height: calc(100% - 24px); */
	position: relative;
	flex: 1 1 auto;
	/* border: solid 1px #bbb; */
	overflow: hidden;
}
</style>